import './LargeModal.scss';

import React, { useEffect, useRef } from 'react';
import { FlightButton, getIcon } from '@flybits/design-system';
import { PortalContainerProvider } from 'components/Shared/LargeModal/PortalContainerContext';

const CLASS_MAIN = 'large-modal';
const CLASSES = {
  CHILDREN: `${CLASS_MAIN}__children`,
  HEADER: `${CLASS_MAIN}__children__header`,
  HEADER_INFO: `${CLASS_MAIN}__children__header__info`,
  HEADER_ICON: `${CLASS_MAIN}__children__header__info__icon`,
  HEADER_TEXT: `${CLASS_MAIN}__children__header__info__text`,
  HEADER_TITLE: `${CLASS_MAIN}__children__header__info__text__title`,
  HEADER_DESCRIPTION: `${CLASS_MAIN}__children__header__info__text__description`,
  CLOSE_ICON: `${CLASS_MAIN}__children__header__close-icon`,
  DIVIDER: `${CLASS_MAIN}__children__divider`,
  BODY: `${CLASS_MAIN}__children__body`,
  FOOTER: `${CLASS_MAIN}__children__footer`,
  FOOTER_ACTIONS: `${CLASS_MAIN}__children__footer__actions`,
};

export interface LargeModalProps extends Omit<React.ComponentProps<'dialog'>, 'children'> {
  isOpen: boolean;
  closeModal: () => void;
  headerIcon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  footer?: React.ReactNode;
  showFooter?: boolean;
  height?: string;
  className?: string;
}

const LargeModal: React.FC<LargeModalProps> = ({
  isOpen,
  closeModal,
  headerIcon,
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
  footer,
  showFooter = true,
  height,
  className = '',
  style,
  ...dialogProps
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const hasFooterActions = !!(primaryAction || secondaryAction);
  const shouldRenderFooter = showFooter && (!!footer || hasFooterActions);

  return (
    <dialog
      ref={dialogRef}
      onCancel={closeModal}
      className={`${CLASS_MAIN} ${className}`}
      style={
        {
          ...(height ? { '--large-modal-height': height } : {}),
          ...style,
        } as React.CSSProperties
      }
      aria-labelledby={`${CLASS_MAIN}-title`}
      {...dialogProps}
    >
      <div className={CLASSES.CHILDREN}>
        <div className={CLASSES.HEADER}>
          <div className={CLASSES.HEADER_INFO}>
            {headerIcon && <div className={CLASSES.HEADER_ICON}>{headerIcon}</div>}
            <div className={CLASSES.HEADER_TEXT}>
              <div className={CLASSES.HEADER_TITLE} id={`${CLASS_MAIN}-title`}>
                {title}
              </div>
              {description && <div className={CLASSES.HEADER_DESCRIPTION}>{description}</div>}
            </div>
          </div>
          <div className={CLASSES.CLOSE_ICON}>
            <button type="button" onClick={closeModal} aria-label="close modal">
              {getIcon('clear', {})}
            </button>
          </div>
        </div>
        <div className={CLASSES.DIVIDER} />
        <div className={CLASSES.BODY}>
          <PortalContainerProvider value={dialogRef}>{children}</PortalContainerProvider>
        </div>
        {shouldRenderFooter && (
          <>
            <div className={CLASSES.DIVIDER} />
            <div className={CLASSES.FOOTER}>
              {footer ?? (
                <div className={CLASSES.FOOTER_ACTIONS}>
                  {secondaryAction && (
                    <FlightButton
                      label={secondaryAction.label}
                      ariaLabel={secondaryAction.label}
                      theme="minor"
                      onClick={secondaryAction.onClick}
                      disabled={secondaryAction.disabled}
                    />
                  )}
                  {primaryAction && (
                    <FlightButton
                      label={primaryAction.label}
                      ariaLabel={primaryAction.label}
                      theme="primary"
                      onClick={primaryAction.onClick}
                      disabled={primaryAction.disabled}
                      loading={primaryAction.loading}
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </dialog>
  );
};

export default LargeModal;
